import React, { useMemo } from "react";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Grid from "components/common/Grid";
import { usePhcTheme } from "../PhcThemeContext";

const COLUMNS = [
  { key: "sector", label: "Sector", flex: 1.6, align: "left" },
  { key: "holdings", label: "Holdings", flex: 0.7, align: "center" },
  { key: "value", label: "Value (₹)", flex: 1, align: "center" },
  { key: "weight", label: "Weight", flex: 0.8, align: "center" },
  { key: "remark", label: "Remark", flex: 1.1, align: "center" },
];

const HIGHLIGHT_LABELS = [
  "Largest Bet",
  "High-Conviction Growth",
  "India Capex Cycle",
];

const formatIndianNumber = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });
};

const formatWeight = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "—";
  const num = Number(value);
  return `${Number.isInteger(num) ? num : num.toFixed(1)}%`;
};

const titleCase = (str = "") =>
  str
    .toLowerCase()
    .split(/[\s_/()-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const formatStockHighlightLine = (stock) => {
  if (!stock) return "";
  const symbol = stock.symbol || stock.name || "";
  const action = stock.recommended_action || stock.action || "";
  const reason = stock.recommended_reason || stock.reason || "";
  const head = [symbol, action].filter(Boolean).join(" · ");
  if (head && reason) return `${head} — ${reason}`;
  return head || reason;
};

const mapHighlightItem = (h) => {
  if (!h) return null;

  const sector = h.sector || "";
  const tag = h.tag || h.remark || "";
  const weightLabel =
    h.weight != null && h.weight !== "" ? formatWeight(h.weight) : "";

  const title =
    h.title ||
    [sector, tag]
      .filter(Boolean)
      .join(" — ")
      .concat(weightLabel ? ` (${weightLabel})` : "");

  const stockLines = (Array.isArray(h.stocks) ? h.stocks : [])
    .map(formatStockHighlightLine)
    .filter(Boolean);

  const body = [
    h.meaning || h.body || h.summary || h.description || "",
    stockLines.join("; "),
  ]
    .filter(Boolean)
    .join(". ");

  if (!title && !body) return null;
  return { title, body, sector };
};

const RemarkBadge = ({ remark, mobile }) => {
  const { theme } = usePhcTheme();
  if (!remark) return <Text sx={{ color: theme.textMuted }}>—</Text>;

  const remarkStyles = theme.remarkStyles || {};
  const matchedKey = Object.keys(remarkStyles).find(
    (key) => key.toLowerCase() === String(remark).trim().toLowerCase(),
  );
  const baseStyle = (matchedKey && remarkStyles[matchedKey]) || {
    color: theme.textSecondary,
    bg: theme.badgeBg,
    border: theme.sectionBorder,
  };
  const style = remark === "FLAT" ? { ...baseStyle, color: baseStyle.color } : baseStyle;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: mobile ? "13px" : 1,
        py: mobile ? "5px" : 0.75,
        borderRadius: "9999px",
        bgcolor: style.bg,
        border: mobile ? `1px solid ${style.border}` : "none",
      }}
    >
      <Text
        sx={{
          fontSize: mobile ? 10 : 12,
          fontWeight: mobile ? 700 : 500,
          color: style.color,
          whiteSpace: "nowrap",
          lineHeight: mobile ? "15px" : 1.2,
          letterSpacing: mobile ? "0.5px" : "normal",
          textTransform: "uppercase",
        }}
      >
        {remark}
      </Text>
    </Box>
  );
};

/** Mobile sector card —  */
const MobileSectorCard = ({ row }) => {
  const { theme } = usePhcTheme();
  return (
  <Box
    sx={{
      width: "100%",
      borderRadius: "12px",
      border: theme.mobileCardBorder,
      bgcolor: theme.mobileCardBg,
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      overflow: "hidden",
    }}
  >
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{ p: 2 }}
    >
      <Box>
        <Text
          sx={{
            fontWeight: 600,
            fontSize: 14,
            color: theme.mobileLabelColor,
            letterSpacing: "0.7px",
            textTransform: "uppercase",
            lineHeight: "16px",
            mb: "4px",
          }}
        >
          SECTOR
        </Text>
        <Text
          sx={{
            fontWeight: 500,
            fontSize: 24,
            color: theme.mobileValueColor,
            lineHeight: "32px",
          }}
        >
          {titleCase(row.sector) || "—"}
        </Text>
      </Box>
      <RemarkBadge remark={row.remark} mobile />
    </Stack>

    <Box
      sx={{
        borderTop: `1px solid ${theme.mobileMetricBorder}`,
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 1,
        px: 2,
        pt: "17px",
        pb: 2,
      }}
    >
      <Box>
        <Text
          sx={{
            fontSize: 11,
            color: theme.mobileLabelColor,
            textTransform: "uppercase",
            lineHeight: "16.5px",
            mb: "-0.5px",
          }}
        >
          HOLDINGS
        </Text>
        <Text
          sx={{
            fontSize: 18,
            color: theme.mobileValueColor,
            lineHeight: "28px",
          }}
        >
          {row.holdings != null && row.holdings !== "" ? row.holdings : "—"}
        </Text>
      </Box>
      <Box>
        <Text
          sx={{
            fontSize: 11,
            color: theme.mobileLabelColor,
            textTransform: "uppercase",
            lineHeight: "16.5px",
            mb: "-0.5px",
          }}
        >
          VALUE (₹)
        </Text>
        <Text
          sx={{
            fontSize: 18,
            color: theme.mobileValueColor,
            lineHeight: "28px",
          }}
        >
          {formatIndianNumber(row.value)}
        </Text>
      </Box>
      <Box sx={{ textAlign: "right" }}>
        <Text
          sx={{
            fontSize: 11,
            color: theme.mobileLabelColor,
            textTransform: "uppercase",
            lineHeight: "16.5px",
            mb: "-0.5px",
            textAlign: "right",
          }}
        >
          WEIGHT
        </Text>
        <Text
          sx={{
            fontSize: 18,
            color: "#2977EC",
            lineHeight: "28px",
            textAlign: "right",
          }}
        >
          {formatWeight(row.weight)}
        </Text>
      </Box>
    </Box>
  </Box>
  );
};

/** Mobile Insight Highlights — hidden with the desktop HIGHLIGHT panel */
// eslint-disable-next-line no-unused-vars
const MobileInsightHighlights = ({ items }) => {
  const { theme } = usePhcTheme();
  return (
  <Box
    sx={{
      width: "100%",
      borderRadius: "12px",
      border: theme.mobileCardBorder,
      bgcolor: theme.mobileCardBg,
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      p: "17px",
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        component="img"
        src="/images/phc-dashboard/insight-sparkle.svg"
        alt=""
        sx={{ width: 13, height: 13, display: "block", flexShrink: 0 }}
      />
      <Text
        sx={{
          fontWeight: 600,
          fontSize: 14,
          color: "#2977EC",
          letterSpacing: "2.8px",
          textTransform: "uppercase",
          lineHeight: "16px",
        }}
      >
        INSIGHT HIGHLIGHTS
      </Text>
    </Stack>

    <Stack spacing={3}>
      {items.map((item, index) => (
        <Box
          key={`${item.title}-${index}`}
          sx={{
            borderLeft: "2px solid rgba(41,119,236,0.3)",
            pl: "18px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Text
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: theme.mobileValueColor,
              letterSpacing: "0.7px",
              lineHeight: "16px",
            }}
          >
            {item.title}
          </Text>
          {item.body && (
            <Text
              sx={{
                fontWeight: 400,
                fontSize: 16,
                color: theme.mobileLabelColor,
                lineHeight: "26px",
              }}
            >
              {item.body}
            </Text>
          )}
        </Box>
      ))}
    </Stack>
  </Box>
  );
};

/**
 * Sector Diversification
 * Desktop: table + highlight side panel
 * Mobile  — highlights first, then sector cards
 */
const SectorDiversificationTable = ({ sectors = [], highlights }) => {
  const { theme } = usePhcTheme();
  const rows = useMemo(() => sectors || [], [sectors]);

  const highlightItems = useMemo(() => {
    if (Array.isArray(highlights) && highlights.length > 0) {
      return highlights.map(mapHighlightItem).filter(Boolean);
    }

    return rows.slice(0, 3).map((row, index) => {
      const stockNames = (row.stocks || [])
        .map((s) => s.name || s.symbol)
        .filter(Boolean);
      return {
        title: `${titleCase(row.sector)} — ${HIGHLIGHT_LABELS[index] || "Highlight"} (${formatWeight(
          row.weight,
        )})`,
        body:
          stockNames.length > 0
            ? `Holdings include ${stockNames.slice(0, 5).join(", ")}${
                stockNames.length > 5 ? " and others" : ""
              }.`
            : `Key allocation in ${titleCase(row.sector)} at ${formatWeight(
                row.weight,
              )} of the portfolio.`,
      };
    });
  }, [highlights, rows]);

  // eslint-disable-next-line no-unused-vars
  const showHighlights = highlightItems.some((h) => h.body || h.title);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: { xs: 0, md: theme.radiusSection },
        border: {
          xs: "none",
          md: `1px solid ${theme.sectionBorder}`,
        },
        backgroundImage: { xs: "none", md: theme.sectionBg },
        backgroundColor: { xs: "transparent", md: theme.cardBg },
        boxShadow: { xs: "none", md: theme.cardShadow },
        p: { xs: 0, md: 4 },
        overflow: "hidden",
        "&::after": {
          content: { xs: "none", md: '""' },
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ position: "relative", zIndex: 1, mb: { xs: 3, md: 3 } }}>
        <Text
          sx={{
            fontWeight: 600,
            fontSize: { xs: 24, md: 24 },
            color: { xs: theme.mobileValueColor, md: theme.textPrimary },
            lineHeight: { xs: "32px", md: 1.2 },
            mb: { xs: 1, md: 0 },
          }}
        >
          Sector Diversification
        </Text>
        <Text
          sx={{
            display: { xs: "block", md: "none" },
            fontWeight: 400,
            fontSize: 16,
            color: theme.mobileLabelColor,
            lineHeight: "24px",
          }}
        >
          Jarvis - Portfolio Performance Analysis
        </Text>
      </Box>

      {/* —— MOBILE: highlights first, then sector cards —— */}
      <Stack
        spacing={2}
        sx={{ display: { xs: "flex", md: "none" }, position: "relative", zIndex: 1 }}
      >
        {/* {showHighlights && <MobileInsightHighlights items={highlightItems} />} */}

        <Stack spacing="16px" sx={{ pt: 0 }}>
          {rows.map((row, index) => (
            <MobileSectorCard key={`${row.sector}-m-${index}`} row={row} />
          ))}
        </Stack>
      </Stack>

      {/* —— DESKTOP: table + highlight panel —— */}
      <Grid
        container
        spacing={1.5}
        sx={{
          display: { xs: "none", md: "flex" },
          position: "relative",
          zIndex: 1,
          alignItems: "flex-start",
        }}
      >
        <Grid item xs={12} lg={12}>
          <Box
            sx={{
              position: "relative",
              borderRadius: theme.radiusCard,
              border: `1px solid ${theme.sectionBorder}`,
              backgroundImage: theme.sectionBg,
              backgroundColor: theme.cardBg,
              boxShadow: theme.cardShadow,
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                pointerEvents: "none",
                boxShadow: theme.insetGlow,
              },
            }}
          >
            <Box sx={{ overflowX: "auto", position: "relative", zIndex: 1 }}>
              <Box sx={{ minWidth: 640 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: 55,
                    px: 3,
                    bgcolor: theme.tableHeaderSolid || "rgba(0,126,240,0.2)",
                    borderTopLeftRadius: theme.radiusCard,
                    borderTopRightRadius: theme.radiusCard,
                  }}
                >
                  {COLUMNS.map((col) => (
                    <Box
                      key={col.key}
                      sx={{
                        flex: col.flex,
                        textAlign: col.align,
                        px: 0.5,
                      }}
                    >
                      <Text
                        sx={{
                          fontSize: 18,
                          fontWeight: 600,
                          color: theme.tableHeaderText || theme.textPrimary,
                        }}
                      >
                        {col.label}
                      </Text>
                    </Box>
                  ))}
                </Box>

                {rows.map((row, index) => (
                  <Box
                    key={`${row.sector}-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: 68,
                      px: 3,
                      borderBottom:
                        index < rows.length - 1
                          ? `1px solid ${theme.sectionBorder}`
                          : "none",
                    }}
                  >
                    <Box sx={{ flex: COLUMNS[0].flex, px: 0.5 }}>
                      <Text
                        sx={{
                          fontSize: 16,
                          fontWeight: 500,
                          color: theme.textPrimary,
                          textTransform: "uppercase",
                        }}
                      >
                        {row.sector || "—"}
                      </Text>
                    </Box>
                    <Box
                      sx={{
                        flex: COLUMNS[1].flex,
                        px: 0.5,
                        textAlign: "center",
                      }}
                    >
                      <Text sx={{ fontSize: 18, color: theme.textPrimary }}>
                        {row.holdings != null && row.holdings !== ""
                          ? row.holdings
                          : "—"}
                      </Text>
                    </Box>
                    <Box
                      sx={{
                        flex: COLUMNS[2].flex,
                        px: 0.5,
                        textAlign: "center",
                      }}
                    >
                      <Text sx={{ fontSize: 18, color: theme.textPrimary }}>
                        {formatIndianNumber(row.value)}
                      </Text>
                    </Box>
                    <Box
                      sx={{
                        flex: COLUMNS[3].flex,
                        px: 0.5,
                        textAlign: "center",
                      }}
                    >
                      <Text sx={{ fontSize: 18, color: theme.textPrimary }}>
                        {formatWeight(row.weight)}
                      </Text>
                    </Box>
                    <Box
                      sx={{
                        flex: COLUMNS[4].flex,
                        px: 0.5,
                        textAlign: "center",
                      }}
                    >
                      <RemarkBadge remark={row.remark} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* HIGHLIGHT panel hidden — sector table uses full width
        {showHighlights && (
          <Grid item xs={12} lg={4.8}>
            <Box
              sx={{
                position: "relative",
                borderRadius: theme.radiusCard,
                border: `1px solid ${theme.sectionBorder}`,
                backgroundImage: theme.sectionBg,
                backgroundColor: theme.cardBg,
                boxShadow: theme.cardShadow,
                p: 3,
                overflow: "hidden",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "inherit",
                  pointerEvents: "none",
                  boxShadow: theme.insetGlow,
                },
              }}
            >
              <Stack
                spacing={3}
                sx={{ position: "relative", zIndex: 1 }}
              >
                <Text
                  sx={{
                    fontWeight: 700,
                    fontSize: 20,
                    color: theme.textPrimary,
                    letterSpacing: 0.4,
                  }}
                >
                  HIGHLIGHT
                </Text>

                {highlightItems.map((item, index) => (
                  <Box key={`${item.title}-d-${index}`}>
                    <Text
                      sx={{
                        fontWeight: 600,
                        fontSize: 18,
                        color: theme.textPrimary,
                        mb: 1,
                      }}
                    >
                      {item.title}
                    </Text>
                    {item.body && (
                      <Text
                        sx={{
                          fontWeight: 400,
                          fontSize: 14,
                          color: theme.textMuted,
                          lineHeight: 1.45,
                        }}
                      >
                        {item.body}
                      </Text>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>
        )}
        */}
      </Grid>
    </Box>
  );
};

export default SectorDiversificationTable;
